import { useEffect, useState } from "react";
import { Button, Empty, Input, Modal, Space, Switch, Table, Tag, Tooltip, Typography, notification } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ApiService from "../../core/service/api-service";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import { dateFormat } from "../../core/utils/date-utils";

/**
 * "Listelerim" — liste yönetimi (oluştur / yeniden adlandır / gizli-açık / sil).
 *
 * Her satırdaki aksiyonlar SUNUCUDAN gelen `rights` bayraklarına göre gösterilir:
 * yeniden adlandırma ve gizli/açık yalnız sahipte, silme sahip veya moderatörde (LIST_VIEW_ALL).
 * UI bu kararı kendi hesaplamaz — kural tek yerde (StudyListAccessFilterExtension.Rights).
 *
 * Sayfalama yok: bir kullanıcının göreceği liste sayısı onlar mertebesinde; sunucu hepsini
 * tek istekte döndürüyor.
 */
export default function StudyListManageGrid() {
    const { t } = useTranslation();
    const { hasPermission } = useUser();

    const [lists, setLists] = useState([])
    const [loading, setLoading] = useState(false)
    const [creating, setCreating] = useState(false)
    const [newName, setNewName] = useState('')
    const [newPublic, setNewPublic] = useState(false)

    const canCreate = hasPermission(PERMISSIONS.LIST_CREATE);

    const load = async () => {
        setLoading(true)
        try {
            const res = await ApiService.StudyList.GetList();
            if (res.success)
                setLists(res.data || [])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        load()
    }, []);

    const createList = async () => {
        if (!newName.trim())
            return;

        setCreating(true)
        try {
            const res = await ApiService.StudyList.Create({ name: newName.trim(), isPublic: newPublic });
            if (res.success) {
                setNewName('')
                setNewPublic(false)
                await load()
            }
        } finally {
            setCreating(false)
        }
    }

    const rename = (row) => {
        let value = row.name;
        Modal.confirm({
            title: t('Rename List'),
            content: (
                <Input
                    defaultValue={row.name}
                    maxLength={200}
                    onChange={(e) => value = e.target.value}
                />
            ),
            okText: t('Save'),
            cancelText: t('Cancel'),
            onOk: async () => {
                if (!value || !value.trim()) {
                    notification.warning({ message: t('List name is required') })
                    return;
                }

                const res = await ApiService.StudyList.Rename({ id: row.id, name: value.trim() });
                if (res.success)
                    await load()
            }
        });
    }

    const setPublic = async (row, isPublic) => {
        const res = await ApiService.StudyList.SetPublic({ id: row.id, isPublic });
        if (res.success)
            await load()
    }

    const remove = (row) => {
        Modal.confirm({
            title: t('Delete List'),
            // Silme SOFT: üyelikler ve audit korunur, liste yalnız filtre/yıldızdan düşer.
            content: `${row.name} — ${t('The list will be removed from filters and star tooltips. Study memberships are kept.')}`,
            okText: t('Delete'),
            okButtonProps: { danger: true },
            cancelText: t('Cancel'),
            onOk: async () => {
                const res = await ApiService.StudyList.Delete({ id: row.id });
                if (res.success)
                    await load()
            }
        });
    }

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: (text, row) => (
                <Space>
                    <span>{text}</span>
                    {row.rights?.isOwner && <Tag color={'blue'}>{t('Mine')}</Tag>}
                </Space>
            )
        },
        {
            title: t('Visibility'),
            key: 'isPublic',
            width: 160,
            render: (text, row) => (
                row.rights?.canTogglePublic
                    ? (
                        <Space>
                            <Switch
                                size={'small'}
                                checked={row.isPublic}
                                onChange={(checked) => setPublic(row, checked)}
                            />
                            <span>{row.isPublic ? t('Public') : t('Private')}</span>
                        </Space>
                    )
                    : <Tag>{row.isPublic ? t('Public') : t('Private')}</Tag>
            )
        },
        {
            title: t('Owner'),
            dataIndex: 'ownerUserName',
            key: 'ownerUserName',
            width: 160,
            render: (text) => text || '-'
        },
        {
            title: t('Studies'),
            dataIndex: 'itemCount',
            key: 'itemCount',
            width: 90,
            render: (text) => (
                <Tooltip title={t('Total studies in the list. You only see the ones your hospital access allows.')}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Created At'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 140,
            render: (text) => dateFormat(text)
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (text, row) => (
                <Space>
                    {row.rights?.canRename && (
                        <Tooltip title={t('Rename')}>
                            <Button size={'small'} type={'text'} icon={<EditOutlined />} onClick={() => rename(row)} />
                        </Tooltip>
                    )}
                    {row.rights?.canDelete && (
                        <Tooltip title={row.rights?.isOwner ? t('Delete') : t('Delete (moderation)')}>
                            <Button size={'small'} type={'text'} danger icon={<DeleteOutlined />} onClick={() => remove(row)} />
                        </Tooltip>
                    )}
                </Space>
            )
        },
    ];

    return (
        <>
            {canCreate && (
                <Space style={{ marginBottom: 12 }} wrap>
                    <Input
                        placeholder={t('New list name')}
                        value={newName}
                        maxLength={200}
                        style={{ width: 260 }}
                        onChange={(e) => setNewName(e.target.value)}
                        onPressEnter={createList}
                    />
                    <Space>
                        <Switch checked={newPublic} onChange={setNewPublic} size={'small'} />
                        <span>{newPublic ? t('Public') : t('Private')}</span>
                    </Space>
                    <Button
                        type={'primary'}
                        icon={<PlusOutlined />}
                        loading={creating}
                        disabled={!newName.trim()}
                        onClick={createList}
                    >
                        {t('Create List')}
                    </Button>
                </Space>
            )}

            <Table
                rowKey={'id'}
                size={'small'}
                loading={loading}
                columns={columns}
                dataSource={lists}
                pagination={false}
                locale={{
                    emptyText: (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={canCreate ? t('No list yet, create one above') : t('No list yet')}
                        />
                    )
                }}
            />

            <Typography.Paragraph type={'secondary'} style={{ marginTop: 12, fontSize: 12 }}>
                {t('Studies are added to lists from the Studies screen: click the study id and choose "Add to List".')}
            </Typography.Paragraph>
        </>
    )
}
